// frontend/src/components/chat/NewChatModal.js
// Modal para crear nueva conversacion

import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import './NewChatModal.css';

const NewChatModal = ({ onClose, onCreate, condominiumId, currentUserId }) => {
    const [mode, setMode] = useState('individual'); // 'individual' | 'group'
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);

    // Buscar usuarios
    useEffect(() => {
        const searchUsers = async () => {
            if (searchQuery.length < 2) {
                setSearchResults([]);
                return;
            }

            setSearching(true);
            try {
                const response = await apiRequest(`${API_ENDPOINTS.chatSearchUsers}?q=${encodeURIComponent(searchQuery)}`);
                if (response.success) {
                    setSearchResults(response.data);
                }
            } catch (error) {
                console.error('Error en busqueda:', error);
            } finally {
                setSearching(false);
            }
        };

        const debounce = setTimeout(searchUsers, 300);
        return () => clearTimeout(debounce);
    }, [searchQuery]);

    // Seleccionar/deseleccionar usuario
    const toggleUser = (user) => {
        if (mode === 'individual') {
            // Para chat individual, crear directamente
            handleCreate(user);
        } else {
            // Para grupo, agregar/quitar de la lista
            setSelectedUsers(prev => {
                const exists = prev.find(u => u.id === user.id);
                if (exists) {
                    return prev.filter(u => u.id !== user.id);
                } else {
                    return [...prev, user];
                }
            });
        }
    };

    // Crear conversacion
    const handleCreate = async (directUser = null) => {
        setLoading(true);
        try {
            if (mode === 'individual' && directUser) {
                onCreate({
                    type: 'individual',
                    targetUserId: directUser.id,
                    display_name: directUser.name
                });
            } else if (mode === 'group') {
                if (!groupName.trim()) {
                    alert('El nombre del grupo es requerido');
                    setLoading(false);
                    return;
                }
                if (selectedUsers.length < 1) {
                    alert('Selecciona al menos un miembro para el grupo');
                    setLoading(false);
                    return;
                }
                onCreate({
                    type: 'group',
                    name: groupName.trim(),
                    description: groupDescription.trim(),
                    memberIds: selectedUsers.map(u => u.id)
                });
            }
        } catch (error) {
            console.error('Error al crear conversacion:', error);
        } finally {
            setLoading(false);
        }
    };

    // Obtener inicial para avatar
    const getInitial = (name) => {
        return name ? name.charAt(0).toUpperCase() : '?';
    };

    return (
        <div className="urbanq-newchat-overlay" onClick={onClose}>
            <div className="urbanq-newchat-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="urbanq-newchat-header">
                    <h3>Nueva conversacion</h3>
                    <button className="urbanq-newchat-close" onClick={onClose}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="urbanq-newchat-tabs">
                    <button
                        className={`urbanq-newchat-tab ${mode === 'individual' ? 'active' : ''}`}
                        onClick={() => { setMode('individual'); setSelectedUsers([]); }}
                    >
                        Chat directo
                    </button>
                    <button
                        className={`urbanq-newchat-tab ${mode === 'group' ? 'active' : ''}`}
                        onClick={() => setMode('group')}
                    >
                        Crear grupo
                    </button>
                </div>

                {/* Contenido */}
                <div className="urbanq-newchat-content">
                    {/* Info de grupo */}
                    {mode === 'group' && (
                        <div className="urbanq-newchat-group-info">
                            <input
                                type="text"
                                placeholder="Nombre del grupo *"
                                value={groupName}
                                onChange={e => setGroupName(e.target.value)}
                                className="urbanq-newchat-input"
                                maxLength={50}
                            />
                            <textarea
                                placeholder="Descripcion (opcional)"
                                value={groupDescription}
                                onChange={e => setGroupDescription(e.target.value)}
                                className="urbanq-newchat-textarea"
                                maxLength={200}
                                rows={2}
                            />
                        </div>
                    )}

                    {/* Usuarios seleccionados */}
                    {mode === 'group' && selectedUsers.length > 0 && (
                        <div className="urbanq-newchat-selected">
                            {selectedUsers.map(user => (
                                <div key={user.id} className="urbanq-newchat-chip">
                                    <span>{user.name}</span>
                                    <button onClick={() => toggleUser(user)}>✕</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Barra de busqueda */}
                    <div className="urbanq-newchat-search">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="urbanq-newchat-search-icon">
                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                        </svg>
                        <input
                            type="text"
                            placeholder="Buscar vecinos por nombre, email o apartamento..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searching && <div className="urbanq-newchat-searching"></div>}
                    </div>

                    {/* Resultados de busqueda */}
                    <div className="urbanq-newchat-results">
                        {searchQuery.length < 2 ? (
                            <div className="urbanq-newchat-hint">
                                <p>Escribe al menos 2 caracteres para buscar</p>
                            </div>
                        ) : searchResults.length === 0 && !searching ? (
                            <div className="urbanq-newchat-empty">
                                <p>No se encontraron usuarios</p>
                            </div>
                        ) : (
                            searchResults.map(user => {
                                const isSelected = selectedUsers.find(u => u.id === user.id);
                                return (
                                    <div
                                        key={user.id}
                                        className={`urbanq-newchat-user ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleUser(user)}
                                    >
                                        <div className="urbanq-newchat-user-avatar">
                                            {user.avatar_url ? (
                                                <img src={user.avatar_url} alt="" />
                                            ) : (
                                                <span className="urbanq-newchat-avatar-initial">
                                                    {getInitial(user.name)}
                                                </span>
                                            )}
                                            {user.is_online && <span className="urbanq-newchat-online"></span>}
                                        </div>
                                        <div className="urbanq-newchat-user-info">
                                            <span className="urbanq-newchat-user-name">{user.name}</span>
                                            <span className="urbanq-newchat-user-detail">
                                                {user.apartment && `Apto ${user.apartment}`}
                                                {user.apartment && user.role && ' · '}
                                                {user.role}
                                            </span>
                                        </div>
                                        {mode === 'group' && (
                                            <div className={`urbanq-newchat-checkbox ${isSelected ? 'checked' : ''}`}>
                                                {isSelected && '✓'}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Footer (solo para grupos) */}
                {mode === 'group' && (
                    <div className="urbanq-newchat-footer">
                        <button
                            className="urbanq-newchat-cancel"
                            onClick={onClose}
                        >
                            Cancelar
                        </button>
                        <button
                            className="urbanq-newchat-create"
                            onClick={() => handleCreate()}
                            disabled={loading || !groupName.trim() || selectedUsers.length < 1}
                        >
                            {loading ? 'Creando...' : 'Crear grupo'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewChatModal;
